import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RequestAccessDialog } from '../components/RequestAccessDialog';
import { toast } from 'sonner';
import { Loader2, Shield, Users, ArrowLeft, Mail, CheckCircle2, Activity, BarChart3, FileCheck, QrCode } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";

export default function LoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [showRequestAccess, setShowRequestAccess] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const user = await login(loginEmail, loginPassword);
      toast.success(`Welcome back, ${user.name}!`);
      const dest = user.role === 'organisation' ? '/admin' : user.role === 'supervisor' ? '/supervisor' : '/dashboard';
      navigate(dest);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email address');
      return;
    }
    setIsLoading(true);
    try {
      await authApi.forgotPassword(forgotEmail);
      setResetSent(true);
      toast.success('Reset link sent! Check your email.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex relative overflow-hidden">
      {/* Decorative animated gradient blobs (full page, behind content) */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-32 h-[28rem] w-[28rem] rounded-full bg-teal-400/40 blur-3xl animate-pulse-slow" />
        <div className="absolute top-1/3 -left-20 h-80 w-80 rounded-full bg-emerald-500/30 blur-3xl animate-blob-float" />
        <div className="absolute bottom-0 left-1/4 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-[30rem] w-[30rem] rounded-full bg-teal-300/35 blur-3xl animate-pulse-slow" />
        <div className="absolute top-10 right-1/3 h-64 w-64 rounded-full bg-cyan-300/25 blur-3xl animate-blob-float" style={{ animationDelay: '3s' }} />
        <div className="absolute top-1/2 right-1/4 h-48 w-48 rounded-full bg-amber-200/30 blur-3xl" />
      </div>

      {/* Floating geometric shapes (full page) */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-[15%] right-[8%] h-3 w-3 rounded-full bg-teal-400/60 animate-float-1" />
        <div className="absolute top-[30%] right-[25%] h-2 w-2 rounded-full bg-emerald-400/70 animate-float-2" />
        <div className="absolute bottom-[20%] right-[12%] h-4 w-4 rounded-full bg-amber-400/50 animate-float-3" />
        <div className="absolute top-[60%] left-[55%] h-2.5 w-2.5 rounded-full bg-cyan-400/60 animate-float-1" style={{ animationDelay: '2s' }} />
        <div className="absolute top-[45%] right-[18%] h-2 w-2 rotate-45 bg-teal-500/40 animate-float-2" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-[35%] right-[30%] h-1.5 w-8 rounded-full bg-emerald-400/40 -rotate-45 animate-float-3" style={{ animationDelay: '4s' }} />
      </div>

      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 p-12 flex-col justify-between relative overflow-hidden">
        {/* Abstract layered shapes inside the dark panel */}
        <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-gradient-to-br from-teal-500/50 to-teal-700/0 blur-2xl animate-pulse-slow" />
          <div className="absolute top-1/2 -left-32 h-96 w-96 rounded-full bg-gradient-to-tr from-emerald-500/40 to-teal-400/15 blur-3xl" />
          <div className="absolute bottom-0 right-1/3 h-72 w-72 rounded-full bg-gradient-to-bl from-indigo-500/35 to-transparent blur-3xl animate-blob-float" />
          <div className="absolute top-1/4 right-1/4 h-48 w-48 rounded-full bg-gradient-to-br from-cyan-400/30 to-teal-600/0 blur-3xl animate-pulse-slow" style={{ animationDelay: '4s' }} />
          {/* Geometric floating circles */}
          <svg className="absolute top-12 right-12 h-40 w-40 opacity-40 animate-spin-slow" viewBox="0 0 200 200" fill="none">
            <circle cx="100" cy="100" r="80" stroke="rgb(45 212 191)" strokeWidth="0.5" />
            <circle cx="100" cy="100" r="60" stroke="rgb(45 212 191)" strokeWidth="0.5" strokeDasharray="2 4" />
            <circle cx="100" cy="100" r="40" stroke="rgb(94 234 212)" strokeWidth="0.5" />
            <circle cx="100" cy="100" r="6" fill="rgb(20 184 166)" />
          </svg>
          {/* Hex / triangle accent group */}
          <svg className="absolute top-1/2 right-8 h-24 w-24 opacity-25" viewBox="0 0 100 100" fill="none">
            <polygon points="50,10 85,30 85,70 50,90 15,70 15,30" stroke="rgb(94 234 212)" strokeWidth="1" fill="none" />
            <polygon points="50,25 70,37 70,63 50,75 30,63 30,37" stroke="rgb(45 212 191)" strokeWidth="0.7" fill="rgb(20 184 166 / 0.1)" />
          </svg>
          {/* Sparkle accents */}
          <svg className="absolute top-32 left-16 h-6 w-6 opacity-60 animate-twinkle" viewBox="0 0 24 24" fill="rgb(94 234 212)">
            <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
          </svg>
          <svg className="absolute bottom-1/3 right-32 h-4 w-4 opacity-50 animate-twinkle" style={{ animationDelay: '1.5s' }} viewBox="0 0 24 24" fill="rgb(45 212 191)">
            <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
          </svg>
          <svg className="absolute top-1/2 left-1/3 h-3 w-3 opacity-70 animate-twinkle" style={{ animationDelay: '0.8s' }} viewBox="0 0 24 24" fill="rgb(165 243 252)">
            <path d="M12 0 L13.5 10.5 L24 12 L13.5 13.5 L12 24 L10.5 13.5 L0 12 L10.5 10.5 Z" />
          </svg>
          {/* Diagonal ribbon */}
          <svg className="absolute bottom-0 left-0 w-full opacity-25" viewBox="0 0 800 400" fill="none" preserveAspectRatio="none">
            <path d="M0,200 Q200,80 400,200 T800,200" stroke="rgb(45 212 191)" strokeWidth="1.5" fill="none" />
            <path d="M0,260 Q200,140 400,260 T800,260" stroke="rgb(94 234 212)" strokeWidth="1" fill="none" strokeDasharray="3 6" />
            <path d="M0,320 Q200,200 400,320 T800,320" stroke="rgb(165 243 252)" strokeWidth="0.7" fill="none" strokeDasharray="1 3" />
          </svg>
          {/* Dotted grid */}
          <div
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage: 'radial-gradient(rgb(255 255 255) 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }}
          />
        </div>

        <div className="relative z-10">
          <img src={LOGO_URL} alt="Visitex" className="h-24 w-auto mb-8" />
          <h1 className="font-heading text-4xl font-bold text-white mb-4 tracking-tight">
            NDIS Report Writing
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed max-w-md">
            Professional documentation tools for disability support workers.
            AI-powered, culturally safe, and NDIS audit-ready.
          </p>
        </div>
        <div className="relative z-10 space-y-5">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-teal-600/20 rounded-lg ring-1 ring-teal-500/30 backdrop-blur-sm">
              <Shield className="h-6 w-6 text-teal-400" />
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">NDIS Compliant</h3>
              <p className="text-slate-400 text-sm">All reports meet NDIS audit standards</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="p-3 bg-teal-600/20 rounded-lg ring-1 ring-teal-500/30 backdrop-blur-sm">
              <Users className="h-6 w-6 text-teal-400" />
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">Culturally Safe</h3>
              <p className="text-slate-400 text-sm">Built-in cultural safety review for all documentation</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="p-3 bg-teal-600/20 rounded-lg ring-1 ring-teal-500/30 backdrop-blur-sm">
              <Activity className="h-6 w-6 text-teal-400" />
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">Behaviour Tracker</h3>
              <p className="text-slate-400 text-sm">AI-powered insights with month-on-month progress comparison</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="p-3 bg-teal-600/20 rounded-lg ring-1 ring-teal-500/30 backdrop-blur-sm">
              <BarChart3 className="h-6 w-6 text-teal-400" />
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">Analytics</h3>
              <p className="text-slate-400 text-sm">Real-time dashboards and custom reports for your organisation</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="p-3 bg-teal-600/20 rounded-lg ring-1 ring-teal-500/30 backdrop-blur-sm">
              <FileCheck className="h-6 w-6 text-teal-400" />
            </div>
            <div>
              <h3 className="text-white font-medium mb-1">Compliance</h3>
              <p className="text-slate-400 text-sm">Audit trails, mandatory checks and weekly digests built in</p>
            </div>
          </div>
        </div>
        <p className="relative z-10 text-slate-400 text-sm italic">
          &copy; Visitex — Less time on reports, more time focusing on what really matters.
        </p>
      </div>

      {/* Right Panel - Auth Forms */}
      <div className="flex-1 flex items-center justify-center p-8 relative z-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <img src={LOGO_URL} alt="Visitex" className="h-20 w-auto mx-auto mb-4" />
          </div>

          {/* Trust strip — only show when not in forgot-password flow */}
          {!showForgotPassword && (
            <div className="hidden lg:flex items-center justify-center gap-6 mb-6 px-4 py-2.5 bg-white/60 backdrop-blur-md rounded-full border border-teal-100 shadow-sm" data-testid="trust-strip">
              <div className="text-center">
                <p className="text-lg font-bold text-teal-700 tabular-nums leading-tight">100%</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wide">Audit-ready</p>
              </div>
              <div className="h-7 w-px bg-slate-200" />
              <div className="text-center">
                <p className="text-lg font-bold text-emerald-600 tabular-nums leading-tight">AI</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wide">Powered</p>
              </div>
              <div className="h-7 w-px bg-slate-200" />
              <div className="text-center">
                <p className="text-lg font-bold text-indigo-600 tabular-nums leading-tight">PWA</p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wide">Offline</p>
              </div>
            </div>
          )}
          
          {showForgotPassword ? (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="space-y-1 pb-4">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setShowForgotPassword(false); setResetSent(false); setForgotEmail(''); }}
                    className="p-0 h-auto text-slate-500 hover:text-slate-900"
                    data-testid="back-to-login-btn"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                  </Button>
                </div>
                <CardTitle className="text-2xl font-heading">Reset Password</CardTitle>
                <CardDescription>
                  {resetSent
                    ? "We've sent you an email with a reset link"
                    : "Enter your email and we'll send you a reset link"
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                {resetSent ? (
                  <div className="text-center py-6" data-testid="reset-sent-confirmation">
                    <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <CheckCircle2 className="h-8 w-8 text-green-600" />
                    </div>
                    <p className="text-slate-600 mb-1">Check your inbox at</p>
                    <p className="font-medium text-slate-900 mb-4">{forgotEmail}</p>
                    <p className="text-sm text-slate-500 mb-6">
                      Click the link in the email to reset your password. The link expires in 1 hour.
                    </p>
                    <Button
                      variant="outline"
                      className="w-full h-12"
                      onClick={() => { setShowForgotPassword(false); setResetSent(false); setForgotEmail(''); }}
                      data-testid="return-to-login-btn"
                    >
                      Return to Sign In
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="forgot-email">Email Address</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          id="forgot-email"
                          data-testid="forgot-email-input"
                          type="email"
                          placeholder="name@visitex.com"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          required
                          className="h-12 pl-10"
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full h-12 bg-teal-600 hover:bg-teal-700 btn-press"
                      disabled={isLoading}
                      data-testid="send-reset-btn"
                    >
                      {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Send Reset Link'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-2xl font-heading">Welcome back</CardTitle>
                <CardDescription>Enter your credentials to access your account</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email"
                      type="email"
                      placeholder="name@visitex.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">Password</Label>
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Input
                      id="login-password"
                      data-testid="login-password"
                      type="password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      className="h-12"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 bg-teal-600 hover:bg-teal-700 btn-press"
                    disabled={isLoading}
                    data-testid="login-submit"
                  >
                    {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Sign In'}
                  </Button>
                </form>

                <div
                  className="mt-6 rounded-lg border border-teal-200 bg-teal-50/70 p-3 flex items-start gap-2 text-sm text-slate-700"
                  data-testid="org-onboarding-hint"
                >
                  <QrCode className="w-4 h-4 mt-0.5 shrink-0 text-teal-600" />
                  <div className="flex-1">
                    <p className="mb-2">
                      Setting up a new organisation? Visitex onboards new NDIS providers
                      by QR invitation.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowRequestAccess(true)}
                      className="text-teal-700 hover:text-teal-800 underline font-medium"
                      data-testid="request-access-btn"
                    >
                      Request access →
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          <RequestAccessDialog open={showRequestAccess} onOpenChange={setShowRequestAccess} />
        </div>
      </div>
    </div>
  );
}
